import {
  getBasketSize,
  getSales,
  PerformanceData,
  PerformanceInsight,
  PerformanceOrderTypes,
} from 'apis/merchant-gateway/insights';

import { OrderType } from '../../types';
import { calculateAvgValue } from '../../utils';

export type CalculatedPerformanceTotals = {
  sales: number;
  orders: number;
  avgBasketValue: number;
  avgBasketItems: number;
};

type CalculatedPerformanceData = Record<PerformanceOrderTypes, CalculatedPerformanceTotals>;

const calculateByType =
  (isVATEnabled: boolean) =>
  (
    acc: CalculatedPerformanceData,
    orderTypeEntry: PerformanceData<PerformanceOrderTypes>,
  ): CalculatedPerformanceData => {
    const { label, total } = orderTypeEntry;
    if (!label || !total) {
      return acc;
    }
    const orderType = label;
    const { avgBasketItems, avgBasketValue, orders, sales }: CalculatedPerformanceTotals = acc[orderType];

    const nextAvgBasketValue = calculateAvgValue({
      previousAvg: avgBasketValue,
      previousCount: orders,
      nextAvg: getBasketSize(total, isVATEnabled),
      nextCount: total.orders,
    });

    const nextAvgBasketItems = calculateAvgValue({
      previousAvg: avgBasketItems,
      previousCount: orders,
      nextAvg: total.avgBasketSizeItems,
      nextCount: total.orders,
    });

    const orderTypeCalculatedValues: CalculatedPerformanceTotals = {
      sales: sales + getSales(total, isVATEnabled),
      orders: orders + total.orders,
      avgBasketValue: nextAvgBasketValue,
      avgBasketItems: nextAvgBasketItems,
    };

    return {
      ...acc,
      [orderType]: orderTypeCalculatedValues,
    };
  };

const calculateDateEntry =
  (isVATEnabled: boolean) =>
  (
    acc: CalculatedPerformanceData,
    dateEntry: PerformanceData<string | null, PerformanceOrderTypes>,
  ): CalculatedPerformanceData => {
    const { split: orderTypeEntries } = dateEntry;
    if (!orderTypeEntries) {
      return acc;
    }
    return orderTypeEntries.reduce(calculateByType(isVATEnabled), acc);
  };

const getInsightTotalsByOrderType = (insight: PerformanceInsight, isVATEnabled: boolean) => {
  const initial: CalculatedPerformanceData = {
    [OrderType.WoltPlus]: { sales: 0, orders: 0, avgBasketValue: 0, avgBasketItems: 0 },
    [OrderType.Regular]: { sales: 0, orders: 0, avgBasketValue: 0, avgBasketItems: 0 },
  };

  return (
    insight.data.subscription.split?.reduce<CalculatedPerformanceData>(calculateDateEntry(isVATEnabled), initial) ??
    initial
  );
};

export default getInsightTotalsByOrderType;
